/**
 * API Client
 * Centralized HTTP request handler with error handling
 */

import { buildUrl, getAuthHeaders, getFormDataHeaders } from './apiConfig';
import API_CONFIG from './apiConfig';

/**
 * Handle API response
 */
const handleResponse = async (response) => {
  const contentType = response.headers.get('content-type');
  const isJson = contentType && contentType.includes('application/json');
  
  let data;
  try {
    if (isJson) {
      data = await response.json();
    } else {
      data = await response.text();
    }
  } catch (parseError) {
    console.error('Failed to parse response:', parseError);
    throw new Error('Invalid response format');
  }
  
  if (!response.ok) {
    // Extract error message from various possible response formats
    let errorMessage = `HTTP error! status: ${response.status}`;
    if (data) {
      if (typeof data === 'string') {
        errorMessage = data;
      } else if (data.message) {
        errorMessage = data.message;
      } else if (data.error) {
        errorMessage = data.error;
      } else if (data.errors && Array.isArray(data.errors)) {
        errorMessage = data.errors.join(', ');
      } else if (typeof data === 'object') {
        errorMessage = JSON.stringify(data);
      }
    }
    
    const error = new Error(errorMessage);
    error.status = response.status;
    error.data = data;
    console.error('API Error Response:', { status: response.status, data, errorMessage });
    throw error;
  }
  
  return data;
};

/**
 * Generic API request handler
 */
const apiRequest = async (endpoint, options = {}) => {
  const url = buildUrl(endpoint);
  console.log('Building API URL:', { endpoint, builtUrl: url, baseUrl: API_CONFIG.baseUrl });
  
  const config = {
    ...options,
    headers: {},
  };
  
  // Handle headers based on request type
  if (options.skipAuth) {
    // No auth headers, but may still need Content-Type for JSON
    if (!options.useFormData && !options.headers?.['Content-Type']) {
      config.headers['Content-Type'] = 'application/json';
    }
  } else if (options.useFormData) {
    // FormData - use form data headers (includes auth but no Content-Type)
    config.headers = getFormDataHeaders();
  } else {
    // Normal JSON request - use auth headers (includes Content-Type and Auth)
    config.headers = getAuthHeaders(true);
  }
  
  // Merge any custom headers passed in options
  config.headers = {
    ...config.headers,
    ...options.headers,
  };
  
  try {
    // buildUrl should already return an absolute URL (http://localhost:3000/api/user/login)
    // But we verify it's absolute to prevent relative URL resolution issues
    if (!url.startsWith('http://') && !url.startsWith('https://')) {
      console.warn('URL is not absolute, this may cause issues:', url);
      // If somehow we got a relative URL, make it absolute
      url = `http://localhost:3000${url.startsWith('/') ? url : `/${url}`}`;
    }
    
    console.log('API Request:', { 
      url,
      method: config.method, 
      body: config.body,
      headers: config.headers 
    });
    
    const response = await fetch(url, config);
    console.log('API Response:', { status: response.status, statusText: response.statusText });
    return await handleResponse(response);
  } catch (error) {
    console.error('API Request Error:', error);
    throw error;
  }
};

/**
 * GET request
 */
export const apiGet = async (endpoint, options = {}) => {
  return apiRequest(endpoint, {
    method: 'GET',
    ...options,
  });
};

/**
 * POST request
 */
export const apiPost = async (endpoint, data = null, options = {}) => {
  const config = {
    method: 'POST',
    ...options,
  };
  
  if (data) {
    if (options.useFormData || data instanceof FormData) {
      config.body = data;
      config.useFormData = true;
    } else {
      // Validate data before stringifying
      if (typeof data !== 'object') {
        throw new Error('Data must be an object or FormData');
      }
      
      // Filter out undefined values but keep null and empty strings
      const cleanedData = Object.entries(data).reduce((acc, [key, value]) => {
        if (value !== undefined) {
          acc[key] = value;
        }
        return acc;
      }, {});
      
      console.log('POST data being sent:', cleanedData);
      config.body = JSON.stringify(cleanedData);
    }
  } else {
    console.warn('POST request with no data');
  }
  
  return apiRequest(endpoint, config);
};

/**
 * PUT request
 */
export const apiPut = async (endpoint, data = null, options = {}) => {
  const config = {
    method: 'PUT',
    ...options,
  };
  
  if (data) {
    if (options.useFormData || data instanceof FormData) {
      config.body = data;
      config.useFormData = true;
    } else {
      config.body = JSON.stringify(data);
    }
  }
  
  return apiRequest(endpoint, config);
};

/**
 * PATCH request
 */
export const apiPatch = async (endpoint, data = null, options = {}) => {
  const config = {
    method: 'PATCH',
    ...options,
  };
  
  if (data) {
    if (options.useFormData || data instanceof FormData) {
      config.body = data;
      config.useFormData = true;
    } else {
      config.body = JSON.stringify(data);
    }
  }
  
  return apiRequest(endpoint, config);
};

/**
 * DELETE request
 */
export const apiDelete = async (endpoint, options = {}) => {
  return apiRequest(endpoint, {
    method: 'DELETE',
    ...options,
  });
};

